import React, { useEffect, useState } from 'react';
import { FileText, Eye, Loader2, ExternalLink, Highlighter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { PdfDocViewer } from '@/components/projects/PdfViewer';
import api from '@/lib/api';

export const DOC_TYPE_LABEL = {
  contract_wo_loi: 'Contract / WO / LOI', boq: 'BOQ', scope_of_work: 'Scope of Work',
  accepted_quotation: 'Accepted Quotation', exclusions: 'Exclusions / Clarifications',
  tender_spec: 'Tender / Specification', other: 'Other',
};
export const DOC_STATUS_LABEL = {
  binding: 'Binding', incorporated: 'Incorporated', supporting: 'Supporting', superseded: 'Superseded',
};
const STATUS_CLS = {
  binding: 'bg-money-50 text-money-700 border-money-200',
  incorporated: 'bg-brand-50 text-brand-700 border-brand-200',
  supporting: 'bg-slate-100 text-slate-600 border-slate-200',
  superseded: 'bg-amber-50 text-amber-700 border-amber-200',
};

/** Fetches the original file (auth-protected) and renders it inline with a text
 *  layer. When `highlight` terms are supplied, the matching passage is
 *  highlighted and scrolled into view. */
export const DocumentViewerDialog = ({ fileId, filename, open, onOpenChange, highlight = [] }) => {
  const [data, setData] = useState(null);   // Uint8Array for react-pdf
  const [url, setUrl] = useState(null);      // object URL for "Open in new tab"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoked = null;
    let active = true;
    if (open && fileId) {
      setLoading(true); setError(false); setData(null); setUrl(null);
      api.get(`/scope-files/${fileId}`, { responseType: 'blob' })
        .then(async (r) => {
          if (!active) return;
          const objUrl = URL.createObjectURL(r.data);
          revoked = objUrl; setUrl(objUrl);
          const buf = await r.data.arrayBuffer();
          if (active) setData(new Uint8Array(buf));
        })
        .catch(() => active && setError(true))
        .finally(() => active && setLoading(false));
    }
    return () => { active = false; if (revoked) URL.revokeObjectURL(revoked); };
  }, [open, fileId]);

  const terms = (highlight || []).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden" data-testid="document-viewer-dialog">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4 text-brand" />
            <span className="truncate">{filename || 'Document'}</span>
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer"
                className="ml-auto text-xs text-brand hover:underline flex items-center gap-1" data-testid="doc-open-new-tab">
                <ExternalLink className="h-3.5 w-3.5" />Open in new tab
              </a>
            )}
          </DialogTitle>
          {terms.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-amber-700" data-testid="doc-highlight-note">
              <Highlighter className="h-3.5 w-3.5" />
              Highlighting the referenced passage{terms.length > 1 ? 's' : ''}
            </div>
          )}
        </DialogHeader>
        <div className="border-t border-slate-200 bg-slate-100 h-[74vh] overflow-hidden">
          {loading && (
            <div className="h-full flex flex-col items-center justify-center text-slate-400" data-testid="doc-viewer-loading">
              <Loader2 className="h-6 w-6 animate-spin" /><span className="text-sm mt-2">Loading document…</span>
            </div>
          )}
          {error && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-slate-500" data-testid="doc-viewer-error">
              <FileText className="h-6 w-6 text-slate-300" />
              <span className="text-sm mt-2">Couldn’t open this document.</span>
            </div>
          )}
          {data && !loading && !error && (
            <PdfDocViewer data={data} highlight={terms} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** A clickable list of the project's source documents. Clicking opens the original PDF. */
export const ScopeDocumentList = ({ documents = [] }) => {
  const [view, setView] = useState(null); // {file_id, filename}
  const viewable = documents.filter((d) => d.file_id);

  if (viewable.length === 0) return null;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="scope-document-list">
        {viewable.map((d, i) => (
          <button
            key={d.file_id || i}
            onClick={() => setView({ file_id: d.file_id, filename: d.filename })}
            className="group flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left hover:border-brand/50 hover:bg-brand-50/40 transition-colors"
            data-testid={`scope-doc-${i}`}
          >
            <div className="rounded-md bg-danger-50 text-danger-600 p-2 shrink-0"><FileText className="h-4 w-4" /></div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 truncate">{d.filename}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] text-slate-400">{DOC_TYPE_LABEL[d.doc_type] || d.doc_type}</span>
                <Badge variant="outline" className={`text-[9px] ${STATUS_CLS[d.status] || STATUS_CLS.supporting}`}>
                  {DOC_STATUS_LABEL[d.status] || d.status}
                </Badge>
                {d.extraction_ok === false && (
                  <span className="text-[9px] text-amber-600">scanned — view only</span>
                )}
              </div>
            </div>
            <Eye className="h-4 w-4 text-slate-300 group-hover:text-brand shrink-0" />
          </button>
        ))}
      </div>
      <DocumentViewerDialog
        fileId={view?.file_id}
        filename={view?.filename}
        open={!!view}
        onOpenChange={(o) => { if (!o) setView(null); }}
      />
    </>
  );
};
